// ============================================================================
// SOLENE — flagship demo dataset (deterministic, best-in-class DTC skincare)
// ----------------------------------------------------------------------------
// Neutral internal representation of the account: a Meta (Facebook) ad account
// with a campaign > adset > ad hierarchy, plus a daily metric series for each
// node over the trailing ~180 days (keyed to real calendar dates).
//
// The mock API maps THIS into whatever wire shapes the app's transforms expect.
// Tune the brand story here: spend scale, blended ROAS, AOV, margins, tiers.
// ============================================================================
import { makeRng, hashSeed, jitter, weekdayFactor, round2, round0 } from './mockUtils';

const GROSS_MARGIN = 0.68;      // skincare contribution margin (rev * margin - spend = profit)
const CONV_RATE = 0.033;        // site conversion rate (orders / sessions)
const ATC_TO_PURCHASE = 0.36;   // orders / add-to-carts
const DAYS = 181;               // trailing window incl. today

type Tier = 'prospecting' | 'retargeting' | 'retention' | 'brand';

interface CampaignSpec {
    name: string;
    tier: Tier;
    objective: string;
    baseRoas: number;   // target ROAS center
    dailySpend: number; // avg daily spend center ($)
    score: number;      // Adaya predictive health score 0-100
    adsets: { name: string; ads: string[] }[];
}

// The account's Meta campaigns. Retargeting/retention run hot; prospecting is
// the volume; brand/awareness runs intentionally low ROAS. Blended lands ~3.3x.
const CAMPAIGN_SPECS: CampaignSpec[] = [
    {
        name: 'Prospecting — Advantage+ Shopping', tier: 'prospecting', objective: 'Sales',
        baseRoas: 2.6, dailySpend: 2400, score: 82,
        adsets: [
            { name: 'ASC — Broad', ads: ['UGC — “My 28-day glow”', 'Static — Hero Serum', 'Video — Routine 15s'] },
            { name: 'ASC — Existing Customers 20%', ads: ['Carousel — Bestsellers', 'UGC — Before/After'] },
        ],
    },
    {
        name: 'Prospecting — Broad (Skincare Intent)', tier: 'prospecting', objective: 'Sales',
        baseRoas: 2.3, dailySpend: 1450, score: 74,
        adsets: [
            { name: 'Broad 25-45 F', ads: ['Video — Founder Story 30s', 'Static — Ingredient Story'] },
            { name: 'Broad 25-45 F — Reels', ads: ['Reel — Dermatologist POV', 'Reel — GRWM'] },
        ],
    },
    {
        name: 'Prospecting — Lookalike 3% Purchasers', tier: 'prospecting', objective: 'Sales',
        baseRoas: 2.9, dailySpend: 1150, score: 85,
        adsets: [
            { name: 'LAL 3% — Purchasers 180D', ads: ['UGC — Sensitive Skin', 'Static — Reviews Wall'] },
            { name: 'LAL 2% — ATC 180D', ads: ['Video — 3-Step Routine', 'Carousel — Kit'] },
        ],
    },
    {
        name: 'Prospecting — UGC Creator Testimonials', tier: 'prospecting', objective: 'Sales',
        baseRoas: 2.5, dailySpend: 900, score: 79,
        adsets: [
            { name: 'Creators — Batch 7', ads: ['UGC — @mayaskin', 'UGC — @dr.lena', 'UGC — @thegoodglow'] },
        ],
    },
    {
        name: 'Retargeting — 7D Site Visitors', tier: 'retargeting', objective: 'Sales',
        baseRoas: 6.4, dailySpend: 620, score: 91,
        adsets: [
            { name: 'VV 7D — DPA', ads: ['DPA — Dynamic', 'Static — “Still thinking?”'] },
        ],
    },
    {
        name: 'Retargeting — Add to Cart 14D', tier: 'retargeting', objective: 'Sales',
        baseRoas: 7.9, dailySpend: 430, score: 93,
        adsets: [
            { name: 'ATC 14D — Offer', ads: ['Static — Free shipping $75', 'UGC — “Don’t miss out”'] },
        ],
    },
    {
        name: 'Retargeting — IG Engagers 30D', tier: 'retargeting', objective: 'Sales',
        baseRoas: 5.1, dailySpend: 380, score: 88,
        adsets: [
            { name: 'IG Eng 30D', ads: ['Reel — Testimonials', 'Carousel — Bundle'] },
        ],
    },
    {
        name: 'Retention — Post-Purchase Upsell', tier: 'retention', objective: 'Sales',
        baseRoas: 9.2, dailySpend: 240, score: 95,
        adsets: [
            { name: 'Buyers 30-90D', ads: ['Static — Replenish', 'UGC — Ritual Upgrade'] },
        ],
    },
    {
        name: 'Prospecting — Bestsellers Catalog (DPA)', tier: 'prospecting', objective: 'Sales',
        baseRoas: 3.1, dailySpend: 720, score: 81,
        adsets: [
            { name: 'Broad DPA', ads: ['DPA — Full Catalog', 'DPA — Bestsellers'] },
        ],
    },
    {
        name: 'Brand — Founders Story (Video)', tier: 'brand', objective: 'Awareness',
        baseRoas: 1.4, dailySpend: 540, score: 63,
        adsets: [
            { name: 'Awareness — Broad', ads: ['Video — Brand Film 60s', 'Video — Sustainability'] },
        ],
    },
];

// ---- id helpers (stable, Meta-style numeric-ish ids) --------------------
const pad = (n: number, len: number) => String(n).padStart(len, '0');
function campId(i: number) { return `239500${pad(i, 4)}`; }
function adsetId(ci: number, ai: number) { return `239600${pad(ci, 2)}${pad(ai, 2)}`; }
function adId(ci: number, ai: number, di: number) { return `239700${pad(ci, 2)}${pad(ai, 2)}${pad(di, 2)}`; }

// ---- date helpers -------------------------------------------------------
function ymd(d: Date) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`;
}
// build the trailing date list ending today (local time)
function buildDates(): string[] {
    const out: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = DAYS - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        out.push(ymd(d));
    }
    return out;
}

export interface Metrics {
    spend: number; adspend: number; revenue: number; orders: number;
    roas: number; cpp: number; aov: number; sessions: number;
    addToCarts: number; pageview: number; conversionRate: number;
    profit: number; scores: number; impressions: number; clicks: number;
}

function metricsFromSpendRoas(spend: number, roas: number, score: number): Metrics {
    const revenue = spend * roas;
    const aov = 96 * (0.92 + 0.16 * (roas / 6));      // AOV drifts up a touch on higher-intent traffic, centers ~$92-100
    const orders = revenue / aov;
    const sessions = orders / CONV_RATE;
    const addToCarts = orders / ATC_TO_PURCHASE;
    const cpp = orders > 0 ? spend / orders : 0;
    const profit = revenue * GROSS_MARGIN - spend;
    const clicks = sessions * 0.94;
    const impressions = clicks / 0.019;               // ~1.9% CTR
    return {
        spend: round2(spend), adspend: round2(spend), revenue: round2(revenue),
        orders: round0(orders), roas: round2(roas), cpp: round2(cpp), aov: round2(aov),
        sessions: round0(sessions), addToCarts: round0(addToCarts), pageview: round0(sessions * 1.35),
        conversionRate: round2(CONV_RATE * 100), profit: round2(profit), scores: round0(score),
        impressions: round0(impressions), clicks: round0(clicks),
    };
}

function emptyMetrics(): Metrics {
    return { spend: 0, adspend: 0, revenue: 0, orders: 0, roas: 0, cpp: 0, aov: 0, sessions: 0, addToCarts: 0, pageview: 0, conversionRate: 0, profit: 0, scores: 0, impressions: 0, clicks: 0 };
}

function sumMetrics(list: Metrics[]): Metrics {
    const t = emptyMetrics();
    for (const m of list) {
        t.spend += m.spend; t.adspend += m.adspend; t.revenue += m.revenue; t.orders += m.orders;
        t.sessions += m.sessions; t.addToCarts += m.addToCarts; t.pageview += m.pageview;
        t.profit += m.profit; t.impressions += m.impressions; t.clicks += m.clicks;
    }
    // recompute ratios from summed absolutes
    t.roas = t.spend > 0 ? round2(t.revenue / t.spend) : 0;
    t.cpp = t.orders > 0 ? round2(t.spend / t.orders) : 0;
    t.aov = t.orders > 0 ? round2(t.revenue / t.orders) : 0;
    t.conversionRate = t.sessions > 0 ? round2((t.orders / t.sessions) * 100) : 0;
    // volume-weighted score
    const wsum = list.reduce((a, m) => a + m.spend, 0);
    t.scores = wsum > 0 ? round0(list.reduce((a, m) => a + m.scores * m.spend, 0) / wsum) : 0;
    ['spend', 'adspend', 'revenue', 'profit'].forEach(k => (t as any)[k] = round2((t as any)[k]));
    ['orders', 'sessions', 'addToCarts', 'pageview', 'impressions', 'clicks'].forEach(k => (t as any)[k] = round0((t as any)[k]));
    return t;
}

export interface AdNode { id: string; name: string; status: string; daily: Record<string, Metrics>; }
export interface AdsetNode { id: string; name: string; status: string; ads: AdNode[]; daily: Record<string, Metrics>; }
export interface CampaignNode { id: string; name: string; status: string; tier: Tier; objective: string; adsets: AdsetNode[]; daily: Record<string, Metrics>; }

export interface Dataset {
    dates: string[];
    campaigns: CampaignNode[];
    accountDaily: Record<string, Metrics>; // account-level totals per day
}

let _cache: Dataset | null = null;

export function getDataset(): Dataset {
    if (_cache) return _cache;
    const dates = buildDates();
    const campaigns: CampaignNode[] = [];

    // gentle global growth trend across the window (account scaling ~1.0 -> ~1.35)
    const growthAt = (idx: number) => 1.0 + 0.35 * (idx / (dates.length - 1));

    CAMPAIGN_SPECS.forEach((spec, ci) => {
        const cRng = makeRng(hashSeed(spec.name));
        const adsets: AdsetNode[] = spec.adsets.map((as, ai) => {
            const ads: AdNode[] = as.ads.map((adName, di) => ({
                id: adId(ci + 1, ai + 1, di + 1), name: adName, status: 'ACTIVE', daily: {},
            }));
            return { id: adsetId(ci + 1, ai + 1), name: as.name, status: 'ACTIVE', ads, daily: {} };
        });

        const camp: CampaignNode = {
            id: campId(ci + 1), name: spec.name, status: 'ACTIVE', tier: spec.tier,
            objective: spec.objective, adsets, daily: {},
        };

        dates.forEach((day, idx) => {
            const dow = new Date(day + 'T00:00:00').getDay();
            const season = weekdayFactor(dow);
            const growth = growthAt(idx);
            // campaign daily spend with weekly seasonality, growth, and organic jitter
            const spend = Math.max(0, spec.dailySpend * season * growth * jitter(cRng, 0.18));
            // efficiency improves ~18% across the window so period-over-period
            // comparisons (Trends / Instant Insights) read as real gains, plus a
            // slow monthly wave so months differ from one another.
            const effTrend = 0.91 + 0.18 * (idx / (dates.length - 1));
            const monthlyWave = 1 + 0.06 * Math.sin((idx / 30.4) * Math.PI);
            const roas = Math.max(0.4, spec.baseRoas * effTrend * monthlyWave * jitter(cRng, 0.12));
            const score = Math.min(99, Math.max(30, spec.score + (cRng() * 2 - 1) * 6));

            // split spend across adsets (fixed-ish weights per adset), then across ads
            const asWeights = adsets.map((_, i) => 0.5 + 0.5 * makeRng(hashSeed(spec.name + '#' + i))());
            const asWsum = asWeights.reduce((a, b) => a + b, 0);

            adsets.forEach((aset, aIdx) => {
                const asSpend = spend * (asWeights[aIdx] / asWsum);
                // adset roas varies slightly around campaign roas
                const asRoas = Math.max(0.4, roas * (0.9 + 0.2 * makeRng(hashSeed(aset.id + day))()));
                const adWeights = aset.ads.map((_, i) => 0.5 + 0.5 * makeRng(hashSeed(aset.id + '@' + i))());
                const adWsum = adWeights.reduce((a, b) => a + b, 0);
                aset.ads.forEach((ad, adIdx) => {
                    const adSpend = asSpend * (adWeights[adIdx] / adWsum);
                    const adRoas = Math.max(0.4, asRoas * (0.9 + 0.2 * makeRng(hashSeed(ad.id + day))()));
                    ad.daily[day] = metricsFromSpendRoas(adSpend, adRoas, score);
                });
                aset.daily[day] = sumMetrics(aset.ads.map(a => a.daily[day]));
            });
            camp.daily[day] = sumMetrics(adsets.map(a => a.daily[day]));
        });

        campaigns.push(camp);
    });

    // account-level daily totals = sum across campaigns
    const accountDaily: Record<string, Metrics> = {};
    dates.forEach(day => {
        accountDaily[day] = sumMetrics(campaigns.map(c => c.daily[day]));
    });

    _cache = { dates, campaigns, accountDaily };
    return _cache;
}

// Sum a node's metrics across a [from,to] inclusive date range (YYYY-MM-DD).
export function sumRange(daily: Record<string, Metrics>, from: string, to: string): Metrics {
    const picked: Metrics[] = [];
    for (const day of Object.keys(daily)) {
        if (day >= from && day <= to) picked.push(daily[day]);
    }
    return sumMetrics(picked);
}

// Return day-by-day metrics for a node within [from,to].
export function seriesRange(daily: Record<string, Metrics>, from: string, to: string): { day: string; totals: Metrics }[] {
    return Object.keys(daily)
        .filter(day => day >= from && day <= to)
        .sort()
        .map(day => ({ day, totals: daily[day] }));
}
